import { Dispatch, SetStateAction, useMemo } from 'react';
import { IndexTabsList, IndexTabTrigger, TabsGroup } from 'ui/tabsGroup';
import { MESSAGE_MEDIUM_FILTERS } from '../../constants';
import { DateRangePickerWithCollapsibleCalendar } from 'ui/datePicker/DateRangePickerWithCollapsibleCalendar';
import moment from 'moment';
import styled from 'styled-components';
import { TriggeredMessagesRequest, TriggeredMessagesResponse } from '../../types';
import { spacing } from 'ui/theme';
import { ALIGNMENT } from 'ui/selects';
import { Select } from 'ui/selectv2/Select';
import { BaseOption } from 'ui/selectv2/types';
import { useDateRangePicker } from '../../hooks';

const StyledRow = styled.div`
display: flex;
justify-content: space-between;
align-items: center;
`;

const StyledGapRow = styled(StyledRow)`
gap: ${spacing.base};
`;

const StyleTabsGroup = styled(TabsGroup)`
  padding: ${spacing.double} 0;
`;

const StyledSelect = styled(Select)`
width: 400px;
`;

const displayText = {
  selectPlaceholder: 'Viewing all campaigns',
};

interface TableToolbarProps {
  messageRequestParams: TriggeredMessagesRequest;
  setMessageRequestParams: Dispatch<SetStateAction<TriggeredMessagesRequest>>;
  campaigns: TriggeredMessagesResponse['campaigns'] | undefined;
}

export const TableToolbar = ({
  messageRequestParams,
  setMessageRequestParams,
  campaigns,
}: TableToolbarProps) => {
  const { dateRange, handleDateChange, handleCancel } = useDateRangePicker({
    startDate: messageRequestParams.startDate,
    endDate: messageRequestParams.endDate,
  });
  const campaignsOptions: BaseOption<string>[] | undefined = useMemo(
    () =>
      campaigns?.map(({ id, name }) => ({
        label: name,
        value: id.toString(),
        description: `CampaignID:${id}`,
      })),
    [campaigns],
  );

  const selectedCampaign = useMemo(
    () =>
      campaignsOptions?.find(
        (catalog) => catalog.value === messageRequestParams.campaignIdOpt?.toString(),
      ),
    [campaignsOptions, messageRequestParams.campaignIdOpt],
  );

  const updateParams = (prop: keyof TriggeredMessagesRequest, value?: string | number | Date) => {
    setMessageRequestParams((prev) => ({ ...prev, [prop]: value, page: 1 }));
  };

  const handleSelectCampaign = (select: BaseOption<string>[]) => {
    const campaignIdOpt = select[0]?.value ? Number(select[0].value) : undefined;
    setMessageRequestParams((prevParams) => ({ ...prevParams, campaignIdOpt, page: 1 }));
  };

  const handleTabChange = (newTab: string) => {
    updateParams('messageMedium', newTab);
  };

  const handleSubmitDateRange = () =>
    setMessageRequestParams((prev) => ({
      ...prev,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    }));
  return (
    <StyledRow>
      <StyleTabsGroup onValueChange={handleTabChange} defaultValue={MESSAGE_MEDIUM_FILTERS[0].id}>
        <IndexTabsList>
          {MESSAGE_MEDIUM_FILTERS.map((medium) => (
            <IndexTabTrigger key={medium.id} value={medium.id} data-test={`tab-${medium.id}`}>
              {medium.label}
            </IndexTabTrigger>
          ))}
        </IndexTabsList>
      </StyleTabsGroup>
      <StyledGapRow>
        <DateRangePickerWithCollapsibleCalendar
          horizontalAlignment={ALIGNMENT.RIGHT}
          startDate={moment(dateRange.startDate)}
          endDate={moment(dateRange.endDate)}
          onDateRangeChange={handleDateChange}
          hasActions
          onSubmit={handleSubmitDateRange}
          onCancel={handleCancel}
          promoteCustomInput
          blockFutureDates
          isTimeZonePickerDisabled
          closeDropdownOnFooterActions
        />
        <StyledSelect
          options={campaignsOptions || []}
          onSelect={handleSelectCampaign}
          value={[selectedCampaign]}
          placeholder={displayText.selectPlaceholder}
          isClearable
        />
      </StyledGapRow>
    </StyledRow>
  );
};
